/**
 * Calendar date helpers.
 *
 * Everything here works in LOCAL date parts, never `toISOString()`. A Date at local
 * midnight converts to the previous day in UTC under BST, which silently shifted every
 * cell in the calendar by one day until it was caught.
 */

/** Local calendar date as YYYY-MM-DD. */
export function dateKey(d: Date): string {
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${month}-${day}`
}

export function addDays(d: Date, n: number): Date {
  const copy = new Date(d)
  copy.setDate(copy.getDate() + n)
  return copy
}

export function addMonths(d: Date, n: number): Date {
  const copy = new Date(d.getFullYear(), d.getMonth() + n, 1)
  return copy
}

/** Monday of the week containing `d`. */
export function startOfWeek(d: Date): Date {
  const copy = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  copy.setDate(copy.getDate() - ((copy.getDay() + 6) % 7))
  return copy
}

export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

/** The 42 cells of a Monday-first month grid, including adjacent-month spill. */
export function monthGrid(month: Date): Date[] {
  const first = startOfWeek(startOfMonth(month))
  return Array.from({ length: 42 }, (_, i) => addDays(first, i))
}

export function sameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()
}

export function isToday(d: Date): boolean {
  return dateKey(d) === dateKey(new Date())
}

export function monthTitle(d: Date): string {
  return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
}

export function dayTitle(d: Date): string {
  return d.toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long',
  })
}

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const
